import type { ComponentProps, FormEvent } from 'react';
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { HomeClubSectionFlow } from 'corgi-types';
import { alignItems, display, flexDirection, themeGet } from 'styled-system';

import Alert from 'common/components/Alert';
import { Grid, GridItem } from 'common/components/Grid';
import { HomeClubLegalText } from 'common/components/HomeClubLegalText';
import { BodyRegularBR1, HeadlineH4, HeadlineH5 } from 'common/components/Typography';
import { MessageType } from 'common/enums/Alert';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { DispatchState } from 'common/types/React';
import type { BaseComponentProps } from '../../types';
import { getSubmitButton } from '../../utils/submit-button';

interface HomeClubContentProps extends BaseComponentProps {
  flow: HomeClubSectionFlow;
  isLoading: boolean;
  errorText: Nullable<string>;
  hasPreviousSubAttemptFailed: boolean;
  headingId: string;
  setErrorText: DispatchState<string>;
  handleModalOpen: () => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const Container = styled(GridItem)<any>`
  ${display};
  ${flexDirection};
  ${alignItems};
`;
const containerStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const Subtitle = styled(HeadlineH5)``;
const subtitleStyles: ComponentProps<typeof Subtitle> = {
  as: 'h2',
  m: 'sp_0',
  mb: 'sp_8',
  textAlign: 'center',
};

const Text = styled(BodyRegularBR1)`
  a {
    color: ${themeGet('colors.sys.secondary.text.default')};
  }
`;
const textStyles: ComponentProps<typeof Text> = {
  as: 'p',
  m: 'sp_0',
  mb: 'sp_16',
  textAlign: 'center',
  color: 'sys.neutral.text.strong',
};

const HomeClubContent = ({
  flow,
  isLoading,
  errorText,
  isLoggedIn,
  trackingProps,
  hasPreviousSubAttemptFailed,
  headingId,
  setErrorText,
  handleSubmit,
  handleModalOpen,
}: HomeClubContentProps) => {
  const { t } = useLocalization();
  const submitButton = getSubmitButton({
    flow,
    isLoading,
    isLoggedIn,
    trackingProps,
    handleSubmit,
    handleModalOpen,
  });

  useEffect(() => {
    if (hasPreviousSubAttemptFailed) {
      setErrorText(t('h24_hc_content_page_failed_request_alert'));
    }
  }, [hasPreviousSubAttemptFailed]);

  return (
    <Grid>
      <Container {...containerStyles}>
        {errorText && (
          <Alert type={MessageType.Error} mb="sp_16" aria-live="polite">
            {errorText}
          </Alert>
        )}
        <HeadlineH4 id={headingId} as="h1" textAlign="center">
          {t('h24_hc_title')}
        </HeadlineH4>
        <Subtitle {...subtitleStyles}>{t('h24_hc_subtitle')}</Subtitle>
        <Text {...textStyles}>{t('h24_hc_description')}</Text>

        {flow === HomeClubSectionFlow.Cart && isLoggedIn && <HomeClubLegalText />}

        {flow !== HomeClubSectionFlow.Signup && submitButton}
      </Container>
    </Grid>
  );
};

export default HomeClubContent;
